import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { HandHeart, CheckCircle2 } from "lucide-react";
import { IMG } from "../data/images";

export default function CSR() {
  const [form, setForm] = useState({
    organization_name: "", contact_person: "", email: "", phone: "",
    csr_budget: "", preferred_state: "", children_to_support: "", message: "", consent: false,
  });
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please accept consent.");
    try {
      await api.post("/csr-enquiry", { ...form, children_to_support: form.children_to_support ? parseInt(form.children_to_support, 10) : null });
      setDone(true);
      toast.success("Thank you. Our CSR team will be in touch.");
    } catch (e) { toast.error("Could not submit. Try again."); }
  };

  return (
    <>
      <SEO title="CSR Partnerships — Help a Child Hear" description="Corporates, NGOs, foundations and trusts can sponsor cochlear implant surgery for children in India through HearClear's CSR programme." />
      <section className="bg-gradient-to-br from-amber-50 via-white to-orange-50 py-14">
        <div className="hc-container grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-800 text-[13px] font-semibold rounded-full mb-4"><HandHeart size={14} /> CSR · Help a Child Hear</div>
            <h1 className="font-serif text-[2.25rem] md:text-[3rem] font-semibold leading-[1.08] text-slate-900">Sponsor a child's cochlear implant</h1>
            <p className="mt-5 text-slate-700 text-[1.05rem]">
              India has thousands of children with severe-to-profound hearing loss who cannot afford
              cochlear implant surgery. HearClear runs structured CSR programmes — verification,
              hospital coordination, outcome tracking and impact reports.
            </p>
            <ul className="mt-6 space-y-2">
              {["Verified case profiles with reports", "Empanelled hospital surgical coordination", "AVT therapy bundled with surgery", "Quarterly outcome reports to sponsors", "Photo/video updates with family consent"].map((x) => <li key={x} className="flex gap-2 text-slate-700 text-[15px]"><CheckCircle2 size={18} className="text-[#0F766E] mt-0.5 shrink-0" />{x}</li>)}
            </ul>
          </div>
          <img src={IMG.familyChild2} alt="Child supported by CSR" className="rounded-3xl shadow-md" />
        </div>
      </section>

      <section className="hc-container py-14 max-w-3xl">
        <h2 className="font-serif text-3xl font-semibold text-slate-900">Partner with HearClear</h2>
        {done ? (
          <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
            <CheckCircle2 size={36} className="text-emerald-600 mx-auto" />
            <div className="mt-3 font-serif text-xl font-semibold text-slate-900">Thank you for your interest</div>
            <p className="mt-2 text-slate-700">Our CSR team will contact you within 2 business days with detailed information and case profiles.</p>
          </div>
        ) : (
          <form data-testid="csr-form" onSubmit={submit} className="mt-7 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="label-hc">Organization name *</label><input data-testid="csr-org" required className="input-hc" value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} /></div>
              <div><label className="label-hc">Contact person *</label><input data-testid="csr-contact" required className="input-hc" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
              <div><label className="label-hc">Email *</label><input data-testid="csr-email" required type="email" className="input-hc" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="label-hc">Phone *</label><input data-testid="csr-phone" required className="input-hc" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><label className="label-hc">CSR budget range</label><input data-testid="csr-budget" className="input-hc" value={form.csr_budget} onChange={(e) => setForm({ ...form, csr_budget: e.target.value })} placeholder="e.g. ₹50L – ₹2Cr" /></div>
              <div><label className="label-hc">Preferred state</label><input data-testid="csr-state" className="input-hc" value={form.preferred_state} onChange={(e) => setForm({ ...form, preferred_state: e.target.value })} /></div>
              <div><label className="label-hc">Children to support</label><input data-testid="csr-children" type="number" min="1" className="input-hc" value={form.children_to_support} onChange={(e) => setForm({ ...form, children_to_support: e.target.value })} /></div>
            </div>
            <div><label className="label-hc">Message</label><textarea data-testid="csr-message" className="input-hc" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
            <label className="flex items-start gap-3 cursor-pointer"><input data-testid="csr-consent" required type="checkbox" className="mt-1 w-5 h-5 accent-[#0369A1]" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} /><span className="text-slate-700 text-[14.5px]">I authorise HearClear to contact our organization with CSR details.</span></label>
            <button data-testid="csr-submit" className="btn-primary">Submit CSR enquiry</button>
          </form>
        )}
      </section>
    </>
  );
}
